import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { User, AuthContextType } from "../types/auth.type";
import { apiService } from "../services";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Wrapper que adapta react-oidc-context a nuestra interfaz de AuthContext
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const oidcAuth = useOidcAuth();
  const forcedLogoutInProgressRef = useRef(false);

  const removeOidcUser = oidcAuth.removeUser;

  // Guard constants for the 401 race-condition safeguard (#66)
  const LOGOUT_LOOP_KEY = "kubiy_401_logout_ts";
  const LOGOUT_LOOP_COUNT_KEY = "kubiy_401_logout_count";
  const LOGOUT_LOOP_WINDOW_MS = 60_000; // 60 s
  const LOGOUT_LOOP_MAX = 2;            // allow at most 2 silent-renew attempts per window

  // Función para manejar logout forzado (usando useCallback para estabilidad)
  const handleForcedLogout = useCallback(async () => {
    if (forcedLogoutInProgressRef.current) {
      return;
    }

    // ── Race-condition guard (issue #66) ─────────────────────────────────────
    // On a brand-new user's first login, Pre-Token Generation fires before
    // PostConfirmation has finished writing custom:tenant_id to Cognito. The
    // backend therefore returns 401, but the user IS authenticated — they just
    // hold a stale token. A silent renew (signinSilent) gets a fresh token that
    // now carries tenant_id, breaking the loop without forcing a full logout.
    //
    // Strategy:
    //  1. Detect the race: OIDC user has a sub but the token lacks tenant_id.
    //  2. Attempt signinSilent (up to LOGOUT_LOOP_MAX times within
    //     LOGOUT_LOOP_WINDOW_MS). On success, return early — the caller will
    //     retry with the refreshed token.
    //  3. If signinSilent fails or the cap is reached, fall through to the
    //     normal full-logout path so we never loop forever.
    const currentOidcUser = oidcAuth.user;
    const hasSub = !!currentOidcUser?.profile?.sub;
    const tokenHasTenantId = (() => {
      try {
        const it = currentOidcUser?.id_token;
        if (!it) return false;
        const claims = JSON.parse(atob(it.split(".")[1]));
        return !!(
          claims["tenant_id"] ||
          claims["custom:tenant_id"] ||
          claims["custom:tenantId"] ||
          claims["tenantId"]
        );
      } catch {
        return false;
      }
    })();

    if (hasSub && !tokenHasTenantId) {
      const nowMs = Date.now();
      const lastTs = parseInt(localStorage.getItem(LOGOUT_LOOP_KEY) || "0", 10);
      const savedCount = parseInt(localStorage.getItem(LOGOUT_LOOP_COUNT_KEY) || "0", 10);
      const withinWindow = nowMs - lastTs < LOGOUT_LOOP_WINDOW_MS;
      const attemptCount = withinWindow ? savedCount : 0;

      if (attemptCount < LOGOUT_LOOP_MAX) {
        localStorage.setItem(LOGOUT_LOOP_KEY, String(nowMs));
        localStorage.setItem(LOGOUT_LOOP_COUNT_KEY, String(attemptCount + 1));
        console.warn(
          `[AuthContext] 401 with missing tenantId — attempting silent token refresh ` +
          `(attempt ${attemptCount + 1}/${LOGOUT_LOOP_MAX}) [#66]`
        );
        try {
          await oidcAuth.signinSilent();
          // Success: new token should carry tenant_id. Reset counter and return
          // so the original request can be retried by the caller.
          localStorage.setItem(LOGOUT_LOOP_COUNT_KEY, "0");
          return;
        } catch (silentError) {
          console.warn(
            "[AuthContext] signinSilent failed, proceeding with forced logout:",
            silentError
          );
          // Fall through to full logout below
        }
      } else {
        console.warn(
          `[AuthContext] 401 loop cap (${LOGOUT_LOOP_MAX}) reached within ` +
          `${LOGOUT_LOOP_WINDOW_MS / 1000}s — forcing full logout [#66]`
        );
        localStorage.removeItem(LOGOUT_LOOP_KEY);
        localStorage.removeItem(LOGOUT_LOOP_COUNT_KEY);
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    forcedLogoutInProgressRef.current = true;
    try {
      await removeOidcUser();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
    } catch (error) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
    }
  }, [removeOidcUser, oidcAuth]);

  // Configurar el callback de logout en ApiService cuando el componente se monta
  useEffect(() => {
    apiService.setLogoutCallback(handleForcedLogout);
  }, [handleForcedLogout]);

  // Verificar periódicamente si el usuario sigue siendo válido
  useEffect(() => {
    if (!oidcAuth.isAuthenticated || !oidcAuth.user) {
      return;
    }

    if (window.location.pathname === "/github-callback") {
      console.log("[AuthContext] Skipping auth checks for GitHub callback");
      return;
    }

    // Verificar cada 30 segundos si el token sigue siendo válido
    const checkInterval = setInterval(async () => {
      try {
        // Intentar obtener el usuario actual de Cognito
        const currentUser = await oidcAuth.user;

        // Si no hay usuario o el token expiró, forzar logout
        if (!currentUser || !currentUser.access_token) {
          await handleForcedLogout();
          clearInterval(checkInterval);
          return;
        }

        // Verificar si el token está expirado
        if (
          currentUser.expires_at &&
          currentUser.expires_at * 1000 < Date.now()
        ) {
          await handleForcedLogout();
          clearInterval(checkInterval);
          return;
        }
      } catch (error) {
        // Si hay error al verificar, forzar logout por seguridad
        await handleForcedLogout();
        clearInterval(checkInterval);
      }
    }, 30000); // Verificar cada 30 segundos

    return () => clearInterval(checkInterval);
  }, [oidcAuth.isAuthenticated, oidcAuth.user, handleForcedLogout]);

  // Escuchar eventos de error de autenticación de react-oidc-context
  useEffect(() => {
    if (window.location.pathname === "/github-callback") {
      console.log("[AuthContext] Skipping error check for GitHub callback");
      return;
    }

    // react-oidc-context puede disparar eventos cuando hay problemas de autenticación
    // Verificar si hay errores en el estado
    if (oidcAuth.error && (oidcAuth.isAuthenticated || oidcAuth.user)) {
      handleForcedLogout();
    }
  }, [oidcAuth.error, handleForcedLogout]);

  // Extraer userId y tenantId de los claims del token (sin imprimir en consola)
  const extractTokenClaims = useCallback(() => {
    if (!oidcAuth.user) {
      return { userId: null, tenantId: null };
    }

    try {
      const it = oidcAuth.user?.id_token;
      if (it) {
        const idTokenClaims = JSON.parse(atob(it.split(".")[1]));
        return {
          userId: idTokenClaims.sub || idTokenClaims.user_id || null,
          tenantId:
            idTokenClaims["custom:tenant_id"] ||
            idTokenClaims["custom:tenantId"] ||
            idTokenClaims.tenantId ||
            null,
        };
      }
    } catch (error) {
      // No imprimir errores relacionados con tokens
      return { userId: null, tenantId: null };
    }
    return { userId: null, tenantId: null };
  }, [oidcAuth.user]);

  // Convertir el usuario de OIDC a nuestro formato de User
  const user: User | null = useMemo(() => {
    if (!oidcAuth.user) {
      return null;
    }

    // Acceder a las propiedades del usuario OIDC desde el profile
    // Cognito envía el nombre y correo en user.profile
    const profile = oidcAuth.user.profile || {};

    // Extraer userId y tenantId de los claims del token
    const claims = extractTokenClaims();

    const convertedUser = {
      id: oidcAuth.user.sub || profile.sub || "",
      email:
        profile.email ||
        oidcAuth.user.email ||
        profile.preferred_username ||
        oidcAuth.user.preferred_username ||
        "",
      name:
        profile.name ||
        profile.given_name ||
        oidcAuth.user.name ||
        oidcAuth.user.given_name ||
        profile.preferred_username ||
        oidcAuth.user.preferred_username ||
        "User",
      avatar: profile.picture || oidcAuth.user.picture,
      createdAt: new Date(
        oidcAuth.user.iat ? oidcAuth.user.iat * 1000 : Date.now(),
      ),
      userId: claims.userId,
      tenantId: claims.tenantId,
    };

    return convertedUser;
  }, [oidcAuth.user, extractTokenClaims]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      await oidcAuth.signinRedirect({
        extraQueryParams: {
          // Puedes agregar parámetros adicionales aquí si es necesario
        },
      });
    } catch (error) {
      throw new Error("Failed to initiate login");
    }
  };

  // Método de logout que inicia el proceso
  const logout = async () => {
    try {
      // Verificar usuario antes del logout
      const user = await oidcAuth.user;
      if (!user) {
        window.location.href = "/";
        return;
      }

      await oidcAuth.signoutRedirect({
        extraQueryParams: {
          client_id: process.env.REACT_APP_COGNITO_CLIENT_ID, // El ID de tu aplicación
          logout_uri: `${window.location.origin}/logout-callback`, // La URL de retorno
        },
      });

      //Borrar el usuario de react-oidc-context
      await oidcAuth.removeUser();
      //Limpiar el localStorage
      localStorage.clear();
      //Limpiar el sessionStorage
      sessionStorage.clear();
      //Redirigir a la página de login
      window.location.href = "/";
    } catch (error) {
      // Limpiar y redirigir incluso si hay error
      await oidcAuth.removeUser();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
    }
  };

  const signup = async (): Promise<void> => {
    try {
      // Redirigir a Cognito Hosted UI con screen_hint=signup para mostrar la página de registro
      await oidcAuth.signinRedirect({
        extraQueryParams: {
          screen_hint: "signup",
        },
      });
    } catch (error) {
      throw new Error("Failed to initiate signup");
    }
  };

  // Obtener el access token del usuario OIDC
  const accessToken = useMemo(() => {
    if (!oidcAuth.user) {
      return null;
    }
    // react-oidc-context almacena el token en user.access_token
    return oidcAuth.user.access_token || null;
  }, [oidcAuth.user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: oidcAuth.isAuthenticated,
    isLoading: oidcAuth.isLoading,
    login,
    logout,
    signup,
    accessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
